/**
 * VISIONS (1.2.0) on the Volunteers page: ABF's own participation record (everyone, every Companion activity),
 * who stays off the VISIONS report and why, the monthly report's Review (rows, Fill the workbook, the cover
 * page, Send), Log an activity (REF, WP, OTH…) and the referral task's Mark made. Nothing is emailed until a
 * person presses Send, and then only after the cancel window.
 */
import { useCallback, useId, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { VisionsReportView } from '@shared/visions';
import type { CompanionItem } from '@shared/volunteers-api';
import { heldBackWords } from '@shared/visions';
import { staffName } from '@shared/people';
import type { TenantSummary } from '@shared/api-types';
import { apiFetch } from '@/lib/api';
import { PrintArea } from '@/lib/docs';
import { formatDayKey, formatInstant } from '@/lib/dates';
import { useAnnounce } from '@/components/Announcer';
import { ChangeInSettings } from '@/components/ChangeInSettings';
import { Icon } from '@/components/Icons';
import { TenantSearch } from '@/components/TenantSearch';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import { VisionsCover } from '@/features/print/Printables';
import { errorText } from '@/features/people/api';
import { SelectField, SwitchField, TextField, todayIso } from '@/features/people/fields';
import { postJson, useVolunteerRefresh } from './api';
import { EntrySheet, type CloseoutEntry } from './CompanionCloseout';

export function useVisionsReport(month: string | null) {
  return useQuery({ queryKey: ['visions-report', month], queryFn: () => apiFetch<VisionsReportView>(`/api/volunteers/visions/${month}`), enabled: !!month, staleTime: 30_000, refetchOnWindowFocus: true });
}

/** yyyymm for this month and the eleven before it. */
function recentMonths(todayKey: string): string[] {
  const out: string[] = [];
  let y = Number(todayKey.slice(0, 4));
  let m = Number(todayKey.slice(4, 6));
  for (let i = 0; i < 12; i++) {
    out.push(`${y}${String(m).padStart(2, '0')}`);
    m -= 1;
    if (!m) {
      m = 12;
      y -= 1;
    }
  }
  return out;
}

export function monthLabel(month: string, lang: string): string {
  return new Intl.DateTimeFormat(lang === 'es' ? 'es-US' : 'en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(4, 6)) - 1, 15)));
}

const hours = (minutes: number) => (minutes / 60).toFixed(1);

/** The two VISIONS items on the Companion tab and Today, in the reader's language (the API's words are English). */
export function visionsItemWords(i: CompanionItem, t: TFunction, lang: string): { text: string; sub: string } | null {
  if (i.kind === 'visionsReady') {
    const month = new Intl.DateTimeFormat(lang === 'es' ? 'es-US' : 'en-US', { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(Number(String(i.data.month).slice(0, 4)), Number(String(i.data.month).slice(4, 6)) - 1, 15)));
    return { text: t('vol.visions.readyItem', { month, people: t('vol.visions.peopleN', { count: Number(i.data.people) }), activities: t('vol.visions.activitiesN', { count: Number(i.data.activities) }) }), sub: t('vol.visions.readySub') };
  }
  if (i.kind === 'visionsReferral') return { text: t('vol.visions.referralItem', { name: String(i.data.name), unit: String(i.data.unit) }), sub: t('vol.visions.referralSub') };
  return null;
}

/* ------------------------------------------------------------------------------------------------
 * The card: ABF's participation this month, the VISIONS report, who stays off it
 * ---------------------------------------------------------------------------------------------- */

export function VisionsCard({ todayKey, openMonth, readyMonth, onOpened }: { todayKey: string; openMonth?: string | null; /** 25 Oct 2026 (D-1025-PROGRAM): the month the report is ready for; the card opens on it (`?visions=` still wins) */ readyMonth?: string | null; onOpened?: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const months = useMemo(() => recentMonths(todayKey), [todayKey]);
  const first = [openMonth, readyMonth].find((m): m is string => !!m && months.includes(m)) ?? months[0]!;
  // the ready month arrives with the page's data, so it is the answer until a person picks another month
  const [chosen, setMonth] = useState<string | null>(() => (openMonth && months.includes(openMonth) ? openMonth : null));
  const month = chosen ?? first;
  const q = useVisionsReport(month);
  const [review, setReview] = useState<string | null>(openMonth ?? null);
  const [log, setLog] = useState(false);
  const r = q.data;
  const p = r?.participation;
  return (
    <Card as="section" id="visions" aria-labelledby="h-visions" style={{ marginTop: 18 }} data-testid="visions-card">
      <CardHeader>
        <CardTitle id="h-visions">{t('vol.visions.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <SelectField id="visions-month" label={t('vol.visions.month')} value={month} onChange={(v) => v && setMonth(v)} options={months.map((m) => ({ value: m, label: monthLabel(m, lang) }))} />
        {q.isLoading ? <p className="help">{t('app.loading')}</p> : null}
        {q.isError ? (
          <p className="field-err" role="alert">
            {errorText(q.error)}
          </p>
        ) : null}
        {r && p ? (
          <>
            <h3 className="eyebrow" style={{ marginTop: 12 }}>
              {t('vol.visions.abfTitle')}
            </h3>
            <p data-testid="participation-line">{t('vol.visions.abfLine', { count: p.people, activities: p.activities, hours: hours(p.minutes) })}</p>
            {p.activities ? (
              <div className="table-wrap" tabIndex={0} role="region" aria-label={t('vol.visions.byCodeCaption', { month: monthLabel(month, lang) })}>
                <table className="table" data-testid="participation-table">
                  <caption className="sr-only">{t('vol.visions.byCodeCaption', { month: monthLabel(month, lang) })}</caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('vol.visions.code')}</th>
                      <th scope="col">{t('vol.visions.activities')}</th>
                      <th scope="col">{t('vol.visions.people')}</th>
                      <th scope="col">{t('vol.visions.hours')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.byCode
                      .filter((c) => c.activities)
                      .map((c) => (
                        <tr key={c.code}>
                          <th scope="row">
                            <abbr title={c.description}>{c.code}</abbr> <span className="help">{c.description}</span>
                          </th>
                          <td className="tnum">{c.activities}</td>
                          <td className="tnum">{c.people}</td>
                          <td className="tnum">{hours(c.minutes)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            <p className="help">{t('vol.visions.abfHelp')}</p>

            <h3 className="eyebrow" style={{ marginTop: 12 }}>
              {t('vol.visions.reportTitle')}
            </h3>
            <p data-testid="visions-line">
              {t('vol.visions.reportLine', { count: r.people, activities: r.activities })}
              {r.sent ? ` · ${t('vol.visions.sentOn', { when: formatInstant(r.sent.at, lang), to: r.sent.to })}` : r.versions.length ? ` · ${t('vol.visions.filledNotSent')}` : ''}
            </p>
            {(r.waitingCloseout ?? []).length ? (
              <p className="warn" role="note" data-testid="visions-waiting-line">
                {t('visions1026.waitingTitle', { count: (r.waitingCloseout ?? []).length })}
              </p>
            ) : null}
            <div className="chips">
              <Button size="sm" onClick={() => setReview(month)} data-testid="visions-review">
                {t('vol.visions.review')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setLog(true)} data-testid="visions-log">
                {t('vol.visions.log')}
              </Button>
            </div>

            <h3 className="eyebrow" style={{ marginTop: 12 }} id="h-heldback">
              {t('vol.visions.heldTitle')}
            </h3>
            {r.heldBack.length ? (
              <ul className="attn-list" aria-labelledby="h-heldback" data-testid="visions-heldback">
                {r.heldBack.map((h) => (
                  <li key={h.tenantId}>
                    <span className="dot" style={{ background: 'var(--st-hold-dot)' }} aria-hidden="true" />
                    <div className="t">
                      <b>
                        {h.unit} · {h.name}
                      </b>
                      <span>
                        {heldBackWords(h.why, lang)}
                        {h.activities ? ` · ${t('vol.visions.heldActivities', { count: h.activities })}` : ''}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="help">{t('vol.visions.heldNone')}</p>
            )}
            {r.notConsentedActivities ? <p className="help">{t('vol.visions.notConsented', { count: r.notConsentedActivities })}</p> : null}
          </>
        ) : null}
      </CardContent>
      <Sheet
        open={!!review}
        onOpenChange={(o) => {
          if (!o) {
            setReview(null);
            onOpened?.();
          }
        }}
      >
        {review ? (
          <VisionsReviewSheet
            month={review}
            onClose={() => {
              setReview(null);
              onOpened?.();
            }}
          />
        ) : null}
      </Sheet>
      <Sheet open={log} onOpenChange={(o) => (!o ? setLog(false) : undefined)}>
        {log && r ? <LogActivitySheet codes={r.codes} onClose={() => setLog(false)} /> : null}
      </Sheet>
    </Card>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Review → Fill the workbook → Print the cover → Send
 * ---------------------------------------------------------------------------------------------- */

export function VisionsReviewSheet({ month, onClose }: { month: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const announce = useAnnounce();
  const toast = useToast();
  const qc = useQueryClient();
  const refresh = useVolunteerRefresh();
  const q = useVisionsReport(month);
  const [busy, setBusy] = useState<'fill' | 'send' | null>(null);
  const [printing, setPrinting] = useState(false);
  const [closing, setClosing] = useState<CloseoutEntry | null>(null);
  const donePrinting = useCallback(() => setPrinting(false), []);
  const r = q.data;
  const latest = r?.versions[r.versions.length - 1] ?? null;

  const press = async (what: 'fill' | 'send') => {
    setBusy(what);
    try {
      const res = await postJson<{ report: VisionsReportView; message: string }>(`/api/volunteers/visions/${month}`, { what });
      qc.setQueryData(['visions-report', month], res.report);
      void qc.invalidateQueries({ queryKey: ['visions-report'] });
      refresh();
      toast(res.message);
      announce(res.message);
    } catch (e) {
      const m = errorText(e);
      toast(m);
      announce(m, 'assertive');
    } finally {
      setBusy(null);
    }
  };

  return (
    <SheetContent
      title={t('vol.visions.reviewTitle', { month: monthLabel(month, lang) })}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('vol.visions.close')}
          </Button>
          <Button variant="secondary" disabled={!r || busy !== null} onClick={() => press('fill')} data-testid="visions-fill">
            {latest ? t('vol.visions.fillAgain') : t('vol.visions.fill')}
          </Button>
          <Button disabled={!latest || busy !== null} onClick={() => press('send')} data-testid="visions-send">
            <Icon name="mail" /> {t('vol.visions.send')}
          </Button>
        </>
      }
    >
      {!r ? (
        <p className="help">{q.isError ? errorText(q.error) : t('app.loading')}</p>
      ) : (
        <div data-testid="visions-review-sheet">
          <Sheet
            open={!!closing}
            onOpenChange={(o) => {
              if (!o) {
                setClosing(null);
                void qc.invalidateQueries({ queryKey: ['visions-report'] });
              }
            }}
          >
            {closing ? <EntrySheet entry={closing} onClose={() => {
              setClosing(null);
              void qc.invalidateQueries({ queryKey: ['visions-report'] });
            }} /> : null}
          </Sheet>
          <p>
            <b data-testid="visions-counts">{t('vol.visions.reportLine', { count: r.people, activities: r.activities })}</b>
            {' · '}
            {t('vol.visions.sixtyLine', { count: r.sixtyPlus })} · {t('vol.visions.referralLine', { count: r.referrals })}
          </p>
          <p className="help">{t('vol.visions.rule')}</p>

          {r.rows.length ? (
            <div className="table-wrap" tabIndex={0} role="region" aria-label={t('vol.visions.rowsCaption', { month: monthLabel(month, lang) })}>
              <table className="table" data-testid="visions-rows">
                <caption className="sr-only">{t('vol.visions.rowsCaption', { month: monthLabel(month, lang) })}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t('vol.visions.colName')}</th>
                    <th scope="col">{t('vol.visions.colDate')}</th>
                    <th scope="col">{t('vol.visions.code')}</th>
                    <th scope="col">{t('vol.visions.colMinutes')}</th>
                    <th scope="col">{t('vol.visions.colVolunteer')}</th>
                    <th scope="col">{t('vol.visions.colFlags')}</th>
                    <th scope="col">{t('vol.visions.colNotes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {r.rows.map((x) => (
                    <tr key={x.activityId}>
                      <th scope="row">
                        {x.name}
                        <br />
                        <span className="help">{x.apt}</span>
                      </th>
                      <td>{formatDayKey(x.dayKey, lang)}</td>
                      <td>{x.code}</td>
                      <td className="tnum">{x.minutes ?? '—'}</td>
                      <td>{x.volunteer || '—'}</td>
                      <td>
                        <span className="help">
                          {t('vol.visions.flags', { age: x.age ?? (x.age60 === 'Yes' ? '60+' : x.age60 === 'No' ? t('vol.visions.under60') : '—'), registered: x.registered === 'Yes' ? t('vol.yes') : t('vol.no'), referral: x.referral === 'Yes' ? t('vol.yes') : t('vol.no'), followUp: x.followUp === 'Yes' ? t('vol.yes') : t('vol.no') })}
                        </span>
                      </td>
                      <td lang="en">{x.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="help calm">{t('vol.visions.noRows')}</p>
          )}
          {(r.waitingCloseout ?? []).length ? (
            <section aria-labelledby="h-waiting" data-testid="visions-waiting" style={{ marginTop: 14 }}>
              <h3 className="eyebrow" id="h-waiting">
                {t('visions1026.waitingTitle', { count: (r.waitingCloseout ?? []).length })}
              </h3>
              <p className="help">{t('visions1026.waitingHelp')}</p>
              <ul className="attn-list">
                {(r.waitingCloseout ?? []).map((w) => (
                  <li key={w.appointmentId}>
                    <span className="dot" style={{ background: 'var(--st-hold-dot)' }} />
                    <div className="t">
                      <b>
                        {w.name} · {w.unit}
                      </b>
                      <span>{t('visions1026.waitingLine', { day: formatDayKey(w.dayKey, lang), code: w.code, volunteer: w.volunteer || '—' })}</span>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => setClosing({ appointmentId: w.appointmentId, volunteerId: w.volunteerId, label: `${w.volunteer || w.name} · ${w.unit}`, mode: 'completion' })} data-testid={`waiting-closeout-${w.appointmentId}`} aria-label={t('visions1026.closeOutFor', { name: w.name, day: formatDayKey(w.dayKey, lang) })}>
                      {t('visions1026.closeOut')}
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {r.heldBack.some((h) => h.activities) ? <p className="help">{t('vol.visions.heldCount', { count: r.heldBack.reduce((s, h) => s + h.activities, 0) })}</p> : null}
          {r.notConsentedActivities ? <p className="help">{t('vol.visions.notConsented', { count: r.notConsentedActivities })}</p> : null}

          <h3 className="eyebrow" style={{ marginTop: 14 }}>
            {t('vol.visions.workbook')}
          </h3>
          {r.template === 'missing' ? (
            <p className="warn" role="note">
              {t('vol.visions.noTemplate')} <ChangeInSettings code="visionsTemplate" />
            </p>
          ) : (
            <p className="help">{r.template === 'settings' ? t('vol.visions.templateSettings') : t('vol.visions.templateDefault')}</p>
          )}
          {r.versions.length ? (
            <ul className="plain-list" data-testid="visions-versions">
              {[...r.versions].reverse().map((v) => (
                <li key={v.version}>
                  <a href={`/api/volunteers/visions/${month}/file?v=${v.version}`} download={v.name}>
                    {v.name}
                  </a>{' '}
                  <span className="help">{t('vol.visions.versionLine', { when: formatInstant(v.at, lang), who: staffName(v.by), rows: v.rows })}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="help">{t('vol.visions.notFilled', { folder: r.folder })}</p>
          )}
          {latest ? (
            <Button variant="secondary" size="sm" onClick={() => setPrinting(true)} data-testid="visions-print-cover">
              <Icon name="printer" /> {t('vol.visions.printCover')}
            </Button>
          ) : null}

          <h3 className="eyebrow" style={{ marginTop: 14 }}>
            {t('vol.visions.sendTitle')}
          </h3>
          <p className="help" data-testid="visions-send-help">
            {r.liaison ? t('vol.visions.sendTo', { to: r.liaison }) : t('vol.visions.noLiaison')}{' '}
            {/* 1.6.9: the address is kept in one place — Settings → Who gets what */}
            <ChangeInSettings code="VISIONS_LIAISON_EMAIL" />
          </p>
          {r.sent ? (
            <p role="status" data-testid="visions-sent">
              {t('vol.visions.sentLine', { when: formatInstant(r.sent.at, lang), to: r.sent.to, version: r.sent.version, leaves: formatInstant(r.sent.sendAt, lang) })}
            </p>
          ) : null}

          <PrintArea active={printing} onDone={donePrinting}>
            {printing && latest ? (
              <VisionsCover
                v={{
                  reportingMonth: monthLabel(month, lang),
                  preparedBy: staffName(latest.by),
                  dateSubmitted: formatInstant(latest.at, lang),
                  participants: r.people,
                  consented: r.people,
                  sixtyPlus: r.sixtyPlus,
                  referrals: r.referrals,
                  activities: r.activities,
                }}
              />
            ) : null}
          </PrintArea>
        </div>
      )}
    </SheetContent>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Log an activity (REF, WP, OTH … what the request cards and outings do not produce)
 * ---------------------------------------------------------------------------------------------- */

function LogActivitySheet({ codes, onClose }: { codes: VisionsReportView['codes']; onClose: () => void }) {
  const { t } = useTranslation();
  const uid = useId();
  const announce = useAnnounce();
  const toast = useToast();
  const qc = useQueryClient();
  const [tenant, setTenant] = useState<TenantSummary | null>(null);
  const [day, setDay] = useState(todayIso());
  const [code, setCode] = useState<string>('REF');
  const [minutes, setMinutes] = useState('');
  const [notes, setNotes] = useState('');
  const [followUp, setFollowUp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    if (!tenant) {
      setErr(t('vol.visions.pickTenant'));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const n = minutes.trim() ? Number(minutes) : null;
      const r = await postJson<{ message: string }>('/api/volunteers/visions-activities', { tenantId: tenant.tenantId, day, code, minutes: n !== null && Number.isFinite(n) ? Math.round(n) : null, notes, followUp });
      toast(r.message);
      announce(r.message);
      void qc.invalidateQueries({ queryKey: ['visions-report'] });
      onClose();
    } catch (e) {
      const m = errorText(e);
      setErr(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };
  return (
    <SheetContent
      title={t('vol.visions.logTitle')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('vol.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => save()} data-testid="visions-log-save">
            {t('vol.visions.logSave')}
          </Button>
        </>
      }
    >
      {tenant ? (
        <p>
          <b>
            {tenant.unit} · {tenant.name}
          </b>{' '}
          <Button variant="secondary" size="sm" onClick={() => setTenant(null)}>
            {t('vol.req.change')}
          </Button>
        </p>
      ) : (
        <TenantSearch onOpen={(x) => setTenant(x)} label={t('vol.req.tenant')} placeholder={t('vol.req.tenantPh')} id={`${uid}-tenant`} testId="visions-log-tenant" />
      )}
      <TextField id={`${uid}-day`} label={t('vol.visions.logDay')} type="date" value={day} onChange={setDay} required />
      <SelectField id={`${uid}-code`} label={t('vol.visions.code')} value={code} onChange={(v) => v && setCode(v)} options={codes.map((c) => ({ value: c.code, label: `${c.code} — ${c.description}` }))} />
      <TextField id={`${uid}-min`} label={t('vol.visions.logMinutes')} inputMode="numeric" value={minutes} onChange={setMinutes} help={t('vol.visions.logMinutesHelp')} />
      <TextField id={`${uid}-notes`} label={t('vol.visions.colNotes')} value={notes} onChange={setNotes} maxLength={300} />
      <SwitchField id={`${uid}-fu`} label={t('vol.visions.logFollowUp')} checked={followUp} onChange={setFollowUp} />
      {err ? (
        <p className="field-err" role="alert">
          {err}
        </p>
      ) : null}
      <p className="help">{t('vol.visions.logHelp')}</p>
    </SheetContent>
  );
}

/* ------------------------------------------------------------------------------------------------
 * The referral task: Mark made
 * ---------------------------------------------------------------------------------------------- */

export function ReferralSheet({ tenantId, label, onClose }: { tenantId: string; label: string; onClose: () => void }) {
  const { t } = useTranslation();
  const uid = useId();
  const announce = useAnnounce();
  const toast = useToast();
  const qc = useQueryClient();
  const refresh = useVolunteerRefresh();
  const [day, setDay] = useState(todayIso());
  const [logIt, setLogIt] = useState(true);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await postJson<{ message: string }>('/api/volunteers/visions-referral', { tenantId, day, logActivity: logIt });
      toast(r.message);
      announce(r.message);
      refresh();
      void qc.invalidateQueries({ queryKey: ['visions-report'] });
      void qc.invalidateQueries({ queryKey: ['person', tenantId] });
      onClose();
    } catch (e) {
      announce(errorText(e), 'assertive');
    } finally {
      setBusy(false);
    }
  };
  return (
    <SheetContent
      title={t('vol.visions.referralTitle', { who: label })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('vol.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => save()} data-testid="referral-made">
            {t('vol.visions.markMade')}
          </Button>
        </>
      }
    >
      <p>{t('vol.visions.referralHelp')}</p>
      <TextField id={`${uid}-day`} label={t('vol.visions.referralDay')} type="date" value={day} onChange={setDay} required />
      <SwitchField id={`${uid}-log`} label={t('vol.visions.referralLog')} checked={logIt} onChange={setLogIt} />
    </SheetContent>
  );
}
